import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Tag, X, Lock } from "lucide-react";
import ContactPlanModal from "@/components/ContactPlanModal";

export default function PaywallModal({ open, onClose, onSuccess, expired }) {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("yearly");
  const [promo, setPromo] = useState("");
  const [promoState, setPromoState] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => { if (open) api.get("/billing/plans").then(r => setPlans(r.data)); }, [open]);

  if (!open) return null;

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const r = await api.post("/billing/promo", { code: promo, plan_id: selected });
      setPromoState(r.data);
      r.data.valid ? toast.success(`${r.data.discount_pct}% off applied`) : toast.error(r.data.message);
    } catch { toast.error("Invalid promo"); }
  };

  const subscribe = () => {
    setContactOpen(true);
  };

  const finalAmount = promoState?.valid ? promoState.final_price : (plans.find(p => p.id === selected)?.price || 0);
  const selectedPlan = plans.find(p => p.id === selected);

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="paywall-modal">
      <div className="ai-border rounded-2xl bg-zinc-950 max-w-2xl w-full">
        <div className="rounded-2xl bg-black p-7">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-3">
                <Lock className="w-3 h-3"/> {expired ? "Subscription expired" : "Upgrade"}
              </div>
              <div className="font-display text-3xl tracking-tighter">{expired ? "Renew to keep your AI agents live" : "Pick a plan"}</div>
              <div className="text-zinc-400 mt-1 text-sm">All plans include unlimited AI agents, public chat links, CRM and analytics.</div>
            </div>
            {!expired && <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="paywall-close"><X className="w-5 h-5"/></button>}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {plans.map(p => {
              const sel = selected === p.id;
              return (
                <button key={p.id} onClick={() => { setSelected(p.id); setPromoState(null); }}
                  className={`text-left rounded-xl border p-5 transition-all ${sel ? "bg-zinc-950 border-emerald-500/40 ai-glow" : "bg-zinc-950 border-white/10 hover:border-white/20"}`}
                  data-testid={`paywall-plan-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">{p.name}</div>
                    {sel && <CheckCircle2 className="w-4 h-4 text-emerald-400"/>}
                  </div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-display text-4xl tracking-tighter">₹{p.price.toLocaleString()}</span>
                    <span className="text-zinc-500 text-sm">/{p.interval}</span>
                  </div>
                  {p.savings_pct && <div className="text-xs text-emerald-400 mt-1">Save {p.savings_pct}%</div>}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex gap-2">
            <input value={promo} onChange={e => setPromo(e.target.value.toUpperCase())} placeholder="Promo code (try IP3725)"
              className="flex-1 bg-transparent border border-white/10 rounded-md px-3 h-10 focus:border-white/30 outline-none text-sm font-mono" data-testid="paywall-promo-input"/>
            <Button onClick={applyPromo} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 h-10" data-testid="paywall-promo-apply">
              <Tag className="w-3.5 h-3.5 mr-1"/> Apply
            </Button>
          </div>
          {promoState?.valid && <div className="mt-2 text-xs text-emerald-400">✓ {promoState.code} — saves ₹{promoState.savings}</div>}

          <div className="mt-6 flex items-center justify-between bg-emerald-500/[0.04] border border-emerald-500/20 rounded-xl p-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300">Total today</div>
              <div className="font-display text-2xl tracking-tighter">₹{finalAmount.toLocaleString()}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Mocked Razorpay — instant activation</div>
            </div>
            <Button onClick={subscribe} className="bg-white text-black hover:bg-zinc-200 h-11 px-6" data-testid="paywall-subscribe">
              Subscribe & continue
            </Button>
          </div>
        </div>
      </div>
      <ContactPlanModal open={contactOpen} onClose={() => setContactOpen(false)} planName={selectedPlan?.name}/>
    </div>
  );
}
